import { MapPin, Plus } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { NoteList } from 'src/features/business/notes/components/note-list'
import { NoteModal } from 'src/features/business/notes/components/note-modal'
import { useArchiveReferralShare } from 'src/features/business/referrals/api/referral-shares/use-archive-referral-share'
import { useMutateReferralShareStatus } from 'src/features/business/referrals/api/referral-shares/use-mutate-referral-share-status'
import { useReferralById } from 'src/features/business/referrals/api/referrals/use-referral-by-id'
import { ReferralDetails } from 'src/features/business/referrals/components/referral-details'
import { CompetitionCount } from 'src/features/business/referrals/components/shares/competition-count'
import { logger } from 'src/lib/logger'
import { useReferralShareById } from 'src/shared/api/referrals/use-referral-share-by-id'
import { Archive } from 'src/shared/components/icons/archive'
import { DownArrow } from 'src/shared/components/icons/down-arrow'
import { Hourglass } from 'src/shared/components/icons/hourglass'
import { Note } from 'src/shared/components/icons/note'
import { PointIcon } from 'src/shared/components/icons/point-icon'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Badge } from 'src/shared/components/ui/miscellaneous/badge'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { ConfirmDialog } from 'src/shared/components/ui/popups/confirm-dialog'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { UserCard } from 'src/shared/features/users/components/cards/user-card'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { useToast } from 'src/shared/hooks/use-toast'
import { ChatTypeEnum } from 'src/shared/schemas/chats/chat-data'
import type { Referral } from 'src/shared/schemas/referrals/referral'
import type { ReferralShare } from 'src/shared/schemas/referrals/referral-share'
import { formatTimeDelta, parseISODuration } from 'src/shared/utils/dates/format-time-delta'
import { formatPrintedReferralAddress } from 'src/shared/utils/format-field-value'

const ReceivedReferralSharePage: React.FC = () => {
  const { referralShareId } = useParams<{
    referralShareId: string
  }>()
  const { data: currentUser } = useMyUserData()
  const { data: referralShare } = useReferralShareById(Number(referralShareId))
  const { data: referral } = useReferralById(Number(referralShare?.referralId))
  const isMobile = useIsMobile()
  const updateStatus = useMutateReferralShareStatus()
  const { mutate: archiveReferralShare } = useArchiveReferralShare()
  const { toast } = useToast()
  const [hasTriedExpiring, setHasTriedExpiring] = useState(false)
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false)

  // Calculate time left once for both effect and render
  const parsedTimeLeft = referralShare
    ? typeof referralShare.receiverTimeLeft === 'string'
      ? parseISODuration(referralShare.receiverTimeLeft)
      : referralShare.receiverTimeLeft
    : undefined

  const onPressAddNote = () => {
    setIsNoteModalOpen(true)
  }

  const onPressArchive = (archived: boolean) => {
    archiveReferralShare({ referralShareId: Number(referralShareId), archived })
  }

  // Handle expiration
  useEffect(() => {
    if (!referralShare || hasTriedExpiring) return

    if (referralShare.status === 'pending') {
      if (parsedTimeLeft !== undefined && parsedTimeLeft <= 0) {
        setHasTriedExpiring(true)
        updateStatus.mutateAsync(
          { id: referralShare.id, status: 'expired' },
          {
            onSuccess: () => {
              toast({
                title: 'Referral Expired',
                description: 'This referral has expired and can no longer be updated.',
                variant: 'destructive',
              })
            },
            onError: (error) => {
              logger.error('Failed to expire referral share', error)
              setHasTriedExpiring(false)
            },
          },
        )
      }
    }
  }, [referralShare, updateStatus, toast, parsedTimeLeft, hasTriedExpiring])

  const showPendingView = referralShare?.status === 'pending' && parsedTimeLeft !== undefined && parsedTimeLeft > 0

  if (!referral) {
    throw new Response('Referral not found', { status: 404 })
  }

  if (!currentUser) {
    throw new Response('User not found', { status: 404 })
  }

  if (!referralShare) {
    throw new Response('Referral share not found', { status: 404 })
  }

  const profession = currentUser?.profession

  return (
    <div>
      <div className={`container mb-8 min-h-screen space-y-8 ${isMobile ? 'p-5' : 'p-10'}`}>
        <ReferralHeader
          referral={referral}
          referralShare={referralShare}
          timeLeft={formatTimeDelta(referralShare.receiverTimeLeft, { short: false, future: true })}
          points={referralShare.receiverTotalPoints}
        />

        {/* Action Buttons Section */}
        {referralShare.status === 'approved' && (
          <section className="flex items-start justify-center gap-8">
            <div className="flex flex-col items-center">
              <Button variant="accent" onClick={onPressAddNote} className="mb-2 px-10 py-4">
                <Note className="size-6" />
              </Button>
              <span className="text-sm">Add note</span>
            </div>
            <div className="flex flex-col items-center">
              <Button
                variant="accent"
                onClick={() => onPressArchive((referralShare as any).archived ? false : true)}
                className="mb-2 px-10 py-4"
              >
                <Archive className="size-6" />
              </Button>
              <span className="text-sm">{(referralShare as any).archived ? 'Unarchive' : 'Archive'}</span>
            </div>
          </section>
        )}

        <section className="mb-16 space-y-4">
          <div className="flex items-end space-x-2">
            <h2 className="text-xl font-semibold">Referred by</h2>
            <DownArrow className="size-4 text-muted" />
          </div>
          <UserCard
            userId={referral.createdByUserId}
            buttonType={
              referralShare.status === 'interested' || referralShare.status === 'approved' ? 'chat' : 'profile'
            }
            chatId={`referral-share-${referralShare.id}`}
            chatType={ChatTypeEnum.REFERRAL}
          />
        </section>
        <section className="mb-16 space-y-4">
          <h2 className="text-2xl font-bold">Referral Info</h2>
          <ReferralDetails referralId={referral.id} status={referralShare.status} profession={profession} />
        </section>

        {referralShare.status === 'approved' && (
          <section className="mb-16 space-y-4">
            <div className="flex items-center justify-between space-x-2">
              <h2 className="text-2xl font-bold">Notes</h2>

              <Button
                variant="accent"
                size="icon"
                onClick={onPressAddNote}
                className="size-8 rounded-lg bg-background shadow-sm"
              >
                <Plus className="size-5" />
              </Button>
              <NoteModal
                isOpen={isNoteModalOpen}
                onClose={() => setIsNoteModalOpen(false)}
                referralShareId={Number(referralShareId)}
              />
            </div>
            <NoteList referralShareId={referralShare.id} />
          </section>
        )}
      </div>

      {showPendingView && <InterestDecision referralShareId={referralShare.id} timeLeft={parsedTimeLeft} />}
    </div>
  )
}

//TODO: (refactor) Move header to its own component
const ReferralHeader: React.FC<{
  referral: Referral
  referralShare: ReferralShare
  timeLeft: string
  points?: number
}> = ({ referral, referralShare, timeLeft, points }) => {
  const isMobile = useIsMobile()

  if (referralShare.isClosed) {
    return (
      <section className="mb-2 space-y-4">
        <h2 className="text-xl font-semibold">Referral Closed</h2>
        <p className="text-muted">This referral has been closed and is no longer available.</p>
        <div className="mb-4 flex w-fit items-center rounded-md bg-destructive/10 p-2 text-destructive/70">
          <span>Last Status: {referralShare.status}</span>
        </div>
      </section>
    )
  }

  let content

  switch (referralShare.status) {
    case 'approved':
      content = (
        <div className="flex flex-col items-center gap-4 text-center">
          <h1 className="text-3xl font-semibold">
            {referralShare.clientFirstName} {referralShare.clientLastName}
          </h1>
          <div className="flex items-center justify-center">
            {!isMobile && <MapPin className="mr-1 size-4 text-muted" />}
            <p className=" text-muted">{formatPrintedReferralAddress(referral, false)}</p>
          </div>
          {points && (
            <Badge
              variant="secondary"
              icon={PointIcon}
              text={`You've won ${points} points with this referral`}
              className="font-bold"
            />
          )}
        </div>
      )
      break
    case 'pending':
      content = (
        <>
          <Badge variant={timeLeft.includes('overdue') ? 'destructive' : 'default'} icon={Hourglass} text={timeLeft} />
          <h2 className="text-2xl text-muted">
            Referral from{' '}
            <strong className="text-primary">
              {referralShare.senderFirstName} {referralShare.senderLastName}
            </strong>
          </h2>
          <p className="mb-4 text-muted">
            Please review the referral details and let the sender know if you&apos;re interested!
          </p>
        </>
      )
      break
    case 'interested':
      content = (
        <>
          <Badge variant="secondary" icon={Hourglass} iconColor="text-secondary" text="Waiting on sender's decision" />
          <h2 className="text-2xl text-muted">
            Referral from{' '}
            <strong className="text-primary">
              {referralShare.senderFirstName} {referralShare.senderLastName}
            </strong>
          </h2>
          <div className="flex flex-row flex-wrap items-center gap-8 gap-y-0">
            <div className="mb-2 flex items-center">
              <MapPin className="mr-1 size-4 text-muted" />
              <span className="text-muted">{formatPrintedReferralAddress(referral, true)}</span>
            </div>
            {referralShare.competitionCount > 0 && <CompetitionCount count={referralShare.competitionCount} />}
          </div>
        </>
      )
      break
    case 'expired':
      content = (
        <div className="text-center">
          <h2 className="text-3xl font-semibold">Referral Expired</h2>
          <p className="text-muted">This referral has expired and is no longer available.</p>
        </div>
      )
      break
    case 'sender_denied':
      content = (
        <div className="text-center">
          <h2 className="text-3xl font-semibold">Sender Denied</h2>
          <p className="text-muted">The sender has denied your interest in this referral.</p>
        </div>
      )
      break
    case 'receiver_declined':
      content = (
        <div className="text-center">
          <h2 className="text-3xl font-semibold">Receiver Declined</h2>
          <p className="text-muted">You have declined this referral.</p>
        </div>
      )
      break
    default:
      content = (
        <div className="text-center">
          <h2 className="text-3xl font-semibold text-muted">
            Referral from{' '}
            <strong className="text-primary">
              {referralShare.senderFirstName} {referralShare.senderLastName}
            </strong>
          </h2>
          <div className="flex justify-center">
            <MapPin className="mr-1 size-3 text-muted" />
            <span className="text-primary">No Address Provided</span>
          </div>
          <div className="mx-auto mb-4 flex w-fit items-center rounded-md bg-accent p-2 text-secondary">
            <PointIcon className="mr-1 size-6" />
            <span>You earned 5 points for this referral!</span>
          </div>
        </div>
      )
  }

  return <section className="mb-8 space-y-4">{content}</section>
}

//TODO: (refactor) Move InterestDecision to its own component
//TODO: Add for to interest decline confirmation popup
const InterestDecision: React.FC<{ referralShareId: number; timeLeft: string | number }> = ({
  referralShareId,
  timeLeft,
}) => {
  const updateStatus = useMutateReferralShareStatus()
  const isLoading = updateStatus.isPending
  const [showDeclineConfirmation, setShowDeclineConfirmation] = useState(false)
  const { toast } = useToast()

  const handleInterest = async (status: string) => {
    if (isLoading) return

    // Check if referral has expired
    const secondsLeft = typeof timeLeft === 'string' ? parseISODuration(timeLeft) : timeLeft
    if (secondsLeft <= 0) {
      // Update status to expired
      await updateStatus.mutateAsync(
        { id: referralShareId, status: 'expired' },
        {
          onSuccess: () => {
            toast({
              title: 'Referral Expired',
              description: 'This referral has expired and can no longer be updated.',
              variant: 'destructive',
            })
            window.scrollTo({ top: 0, behavior: 'smooth' })
          },
          onError: (error) => {
            console.error('Error updating expired status:', error)
          },
        },
      )
      return
    }

    // Proceed with normal status update if not expired
    await updateStatus.mutateAsync(
      { id: referralShareId, status },
      {
        onSuccess: () => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
        },
        onError: (error) => {
          console.error('Error updating status:', error)
          toast({
            title: 'Error',
            description: 'Failed to update referral status. Please try again.',
            variant: 'destructive',
          })
        },
      },
    )
  }

  return (
    <>
      <FixedFooter className="border-t bg-accent p-4">
        <div className="flex w-full max-w-2xl flex-row gap-2">
          <Button variant="outline" onClick={() => setShowDeclineConfirmation(true)} className="w-1/2">
            {updateStatus.isPending ? 'Updating...' : 'Decline'}
          </Button>
          <Button
            onClick={() => handleInterest('interested')}
            type="submit"
            variant="secondary"
            className="w-1/2"
            isLoading={isLoading}
          >
            {updateStatus.isPending ? 'Updating...' : "I'm interested"}
          </Button>
        </div>
      </FixedFooter>

      <ConfirmDialog
        open={showDeclineConfirmation}
        onOpenChange={setShowDeclineConfirmation}
        title="Confirm Decline"
        description="Are you sure you want to decline this deal?"
        onConfirm={() => {
          handleInterest('receiver_declined')
          setShowDeclineConfirmation(false)
        }}
      />
    </>
  )
}

export default ReceivedReferralSharePage
